import React, { FC, useState } from "react";
import { Form, Button, Divider, Cascader } from "antd";
import { FeeEarner, DeepPartial, Rate, FeeEarnerLevel } from "../../data-types";
import { PriceInput } from "../input-number";
import styles from "./fee-earner-cascader.css";
import { BaseOptionType } from "antd/lib/cascader";
import { VPDLabel } from "~/context/taxonomy-context";

const {
  ["fee-earner-dropdown"]: feeEarnerDropdownClassName,
  ["divider"]: dividerClassName,
  ["add-rate"]: addRateClassName,
  ["rate-input"]: rateInputClassName,
  ["add-rate-button"]: addRateButtonClassName,
} = styles as any;
interface Option {
  value: string | number;
  label: React.ReactNode | string;
  type?: string;
  disabled?: boolean;
  children?: Option[];
}
type Props = {
  onChange: (values: {
    feeEarnerId: number | null;
    selectedRateId: number;
    feeEarnerLevelId: number | null;
  }) => void;
  onSearch: () => void;
  onAddMatterSpecificRate: (newRate: DeepPartial<Rate>) => void;
  options: Option[];
  selectedFeeEarner?: Pick<FeeEarner, "id" | "feeEarnerLevel" | "name"> | null;
  selectedFeeEarnerLevelId?: number | null;
  selectedLevel?: FeeEarnerLevel;
  isFeeEarnerLevelRate?: boolean;
  selectedRate?: Pick<Rate, "id" | "description" | "amount" | "type" | "currency"> | null;
  loadData?: (selectOptions: BaseOptionType[]) => void;
  matterCurrencySymbol: string;
  loading: boolean;
};

export const FeeEarnerCascader: FC<Props> = ({
  selectedFeeEarner,
  selectedRate,
  options,
  isFeeEarnerLevelRate,
  onChange,
  onSearch,
  onAddMatterSpecificRate,
  selectedLevel,
  loading,
  matterCurrencySymbol,
}) => {
  const FE_LABEL = VPDLabel("FeeEarner");

  const initialState = [];
  const [selectedLevelState] = useState<FeeEarnerLevel | undefined>(selectedLevel);
  const [isOpen, setIsOpen] = useState(false);

  const feeEarnerDescription = isFeeEarnerLevelRate
    ? `${selectedLevel?.description} Rates   👤 `
    : selectedFeeEarner?.name || "";

  const tree2: Option = {
    label: feeEarnerDescription,
    value: isFeeEarnerLevelRate
      ? `level-rate-${selectedLevelState?.id.toString()}`
      : selectedFeeEarner?.id.toString() || "",
    type: "",
  };

  if (selectedFeeEarner || isFeeEarnerLevelRate) {
    initialState.push({
      label: isFeeEarnerLevelRate
        ? selectedLevelState?.description
        : selectedFeeEarner!.feeEarnerLevel?.description || "",
      value: isFeeEarnerLevelRate
        ? selectedLevelState?.id.toString() || ""
        : selectedFeeEarner!.feeEarnerLevel?.id.toString() || "",
      type: "",
    });
    initialState.push(tree2);
    if (selectedRate !== undefined && selectedRate !== null) {
      initialState.push({
        value: selectedRate.id.toString(),
        label: `${selectedRate.currency.symbol} ${selectedRate.amount} (${selectedRate.description})`,
        type: selectedRate.type,
      });
    }
  }

  const [feeEarnerRateSelection, setfeeEarnerRateSelection] = useState<Option[]>(initialState);

  const handleOnFinish = ({ amount }: any) => {
    onAddMatterSpecificRate({
      amount,
      feeEarnerLevelId: parseInt(feeEarnerRateSelection[0]?.value as string),
      feeEarnerId: parseInt(feeEarnerRateSelection[1].value as string),
    });
    form.resetFields();
    setIsOpen(false);
  };

  const [form] = Form.useForm();
  return (
    <Cascader
      open={isOpen}
      onDropdownVisibleChange={(open) => {
        setIsOpen(open);
        if (open) {
          form.resetFields();
        }
      }}
      placeholder={`Select a ${FE_LABEL} 👤`}
      loading={loading}
      showSearch={true}
      options={options}
      className={feeEarnerDropdownClassName}
      value={feeEarnerRateSelection.map((v) => v.value)}
      changeOnSelect
      onChange={(values: any, selectedOptions: any) => {
        setfeeEarnerRateSelection(selectedOptions);
        const parsedValues = values.map((v: any) => parseInt(v as string));
        const isFeeEarnerLevelRate = selectedOptions[1]?.isFeeEarnerLevelRate;
        const feeEarnerLevelId = selectedOptions[1]?.feeEarnerLevel || selectedLevelState?.id;
        if (values.length > 0) {
          onChange({
            feeEarnerId: isFeeEarnerLevelRate ? null : parsedValues[1],
            selectedRateId: parsedValues[2],
            feeEarnerLevelId,
          });
        }
      }}
      onSearch={onSearch}
      dropdownRender={(menu) => (
        <div>
          {menu}
          {feeEarnerRateSelection.length > 0 && (
            <>
              <Divider className={dividerClassName} />
              <Form
                form={form}
                initialValues={{ ["amount"]: "" }}
                className={addRateClassName}
                onFinish={handleOnFinish}
              >
                <Form.Item
                  name="amount"
                  rules={[
                    {
                      validator: (_, value) => {
                        if (!value || value <= 0) {
                          return Promise.reject("Rate must be greater than zero");
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                  style={{ marginBottom: 0, display: "inline-block" }}
                >
                  <PriceInput
                    currencySymbol={matterCurrencySymbol}
                    defaultValue={""}
                    placeholder="Insert matter specific rate"
                    className={rateInputClassName}
                  />
                </Form.Item>
                <Button type="primary" className={addRateButtonClassName} htmlType="submit">
                  OK
                </Button>
              </Form>
            </>
          )}
        </div>
      )}
      displayRender={() => {
        if (!selectedLevel) {
          return `Select a ${FE_LABEL} 👤`;
        }
        if (selectedLevel && !selectedRate) {
          return `Select Team Member & Rate`;
        }
        const rateLabel = selectedRate
          ? `${selectedRate.currency.symbol} ${selectedRate?.amount} (${selectedRate?.description})`
          : "";
        if (isFeeEarnerLevelRate) {
          return (
            <span>
              {selectedLevel?.description} - {rateLabel}
            </span>
          );
        }
        return (
          <span>
            {selectedLevel?.description} ({selectedFeeEarner?.name}) - {rateLabel}
          </span>
        );
      }}
      allowClear={false}
    />
  );
};
